import type { AuthSessionClient } from '@opendoor/auth-fe/context';

import type { useObservability } from '../../helpers/observability';
import { requestZeusFromBrowser, requestZeusReadFromBrowser } from '../api/zeusBrowserClient';
import {
  PasswordResetTokenValidationDocument,
  ResetPasswordWithTokenDocument,
  ViewerSessionDocument,
} from './__generated__/auth.zeus';
import { CustomerActionBlockedError, impersonationSession } from './impersonation';

const loadSession: AuthSessionClient['loadSession'] = async () => {
  const response = await requestZeusReadFromBrowser(ViewerSessionDocument, {}).catch(() => {
    throw new Error('ViewerSession request failed');
  });
  const session = response.viewer.session;
  if (!session) return null;
  const supportCustomerUUID = impersonationSession.getImpersonatedCustomer();
  if (
    supportCustomerUUID &&
    (!session.isImpersonated ||
      session.profile?.customerId?.toLowerCase() !== supportCustomerUUID.toLowerCase())
  ) {
    throw new Error('Support customer session unavailable');
  }
  const profile = session.profile;
  if (!profile || (session.isImpersonated ? !profile.customerId : !profile.email)) {
    throw new Error('ViewerSession profile unavailable');
  }
  const email = profile.email ?? '';
  return {
    customerUuid: profile.customerId,
    email,
    firstName: profile.firstName || profile.fullName || 'Customer',
    fullName: profile.fullName || profile.firstName || 'Customer',
    primaryPhone: profile.primaryPhoneNumberText,
    hasOpendoorEmail: email.endsWith('@opendoor.com'),
    impersonated: session.isImpersonated,
  };
};

export function createAuthClient(
  trackError: ReturnType<typeof useObservability>['trackError'],
): AuthSessionClient {
  return {
    loadSession,
    async validateResetPasswordToken(resetPasswordToken) {
      try {
        const response = await requestZeusFromBrowser(PasswordResetTokenValidationDocument, {
          input: { resetPasswordToken },
        });
        const result = response.account.passwordResetTokenValidation;
        switch (result.__typename) {
          case 'PasswordResetTokenValidationPayload':
            return result.valid ? { success: true } : { success: false };
          case 'InvalidInputError':
          case 'NotFoundError':
          case 'UnauthorizedError':
            return { success: false };
          default:
            result satisfies never;
            return { success: false };
        }
      } catch (error) {
        if (error instanceof CustomerActionBlockedError) return { success: false };
        trackError(
          'validateResetPasswordToken',
          new Error('Reset password token validation failed'),
        );
        return { success: false };
      }
    },
    async resetPassword(resetPasswordToken, newPassword) {
      try {
        const response = await requestZeusFromBrowser(ResetPasswordWithTokenDocument, {
          input: { resetPasswordToken, newPassword },
        });
        const result = response.resetPasswordWithToken;
        switch (result.__typename) {
          case 'CommandResult':
            return result.success ? { success: true } : { success: false };
          case 'InvalidInputError':
          case 'NotFoundError':
          case 'UnauthorizedError':
            return { success: false };
          default:
            result satisfies never;
            return { success: false };
        }
      } catch (error) {
        if (error instanceof CustomerActionBlockedError) return { success: false };
        trackError('resetPassword', new Error('Password reset request failed'));
        return { success: false };
      }
    },
  };
}
